import { Component, OnInit } from "@angular/core";
import {
  DispatchDashboardService,
  DashboardNoteItem,
  DriverStatusSummary,
  DriverListing,
  AccidentBreakdownItem,
} from "./dispatch-dashboard.service";
import { DriverNote } from "@app/shared/models/drivers/DriverViewModel.model";
import { ClientNoteViewModel } from "@app/shared/models/clients/ClientNoteViewModel.model";
import { GetPictureService } from "@app/shared/services/files/getPictures.service";
import { DriverCardData } from "@app/shared/components/driver-card/driver-card.component";
import { authenticationService } from "@app/login/auth.service";
import { Permissions } from "@app/shared/models/permissions.enum";

export type ActiveView = "driver-notes" | "client-notes" | "returning" | "accidents";

@Component({
  selector: "app-dispatch-dashboard",
  templateUrl: "./dispatch-dashboard.component.html",
  styleUrls: ["./dispatch-dashboard.component.css"],
})
export class DispatchDashboardComponent implements OnInit {
  openDriverNotes: DashboardNoteItem[] = [];
  openClientNotes: DashboardNoteItem[] = [];
  driverNotesLoading = true;
  clientNotesLoading = true;

  // Driver notes pagination & sorting
  driverNotesTotalCount = 0;
  driverNotesTileCount = 0;
  driverNotesPage = 0;
  driverNotesPageSize = 25;
  driverNotesSortField = "dateCreatedUtc";
  driverNotesSortDirection: "asc" | "desc" = "desc";
  driverNotesIncludeClosed = false;

  // Accidents/Breakdowns
  accidentBreakdowns: AccidentBreakdownItem[] = [];
  accidentBreakdownsLoading = true;
  accidentTotalCount = 0;
  accidentTileCount = 0;
  accidentPage = 0;
  accidentPageSize = 25;
  accidentIncludeClosed = false;
  accidentSearch = "";
  accidentTypeFilter = "";
  accidentDateFrom = "";
  accidentDateTo = "";

  // Client notes pagination
  clientNotesTotalCount = 0;
  clientNotesPage = 0;
  clientNotesPageSize = 25;
  clientNoteIncludeClosed = false;
  clientNoteSearch = "";
  clientNoteTypeFilter = "";
  clientNoteClientFilter = "";
  clientNoteDateFrom = "";
  clientNoteDateTo = "";

  driverCardCache: { [id: string]: DriverCardData } = {};

  driverStatus: DriverStatusSummary;
  driverStatusLoading = true;

  private static readonly VIEW_KEY = "dispatchDashboard.activeView";
  private static readonly VALID_VIEWS: ActiveView[] = [
    "driver-notes",
    "client-notes",
    "returning",
    "accidents",
  ];

  private get storedView(): ActiveView {
    const v = localStorage.getItem(DispatchDashboardComponent.VIEW_KEY) as ActiveView;
    return DispatchDashboardComponent.VALID_VIEWS.includes(v) ? v : "driver-notes";
  }

  activeView: ActiveView = this.storedView;

  driverNoteModalVisible = false;
  selectedDriverNote: Partial<DriverNote> = null;
  clientNoteModalVisible = false;
  selectedClientNote: ClientNoteViewModel = null;
  noteDetailLoading = false;

  // Filter state
  noteSearch = "";
  noteTypeFilter = "";
  noteClientFilter = "";
  noteDateFrom: string = "";
  noteDateTo: string = "";

  constructor(
    private dashboardService: DispatchDashboardService,
    private getPictureService: GetPictureService,
    private authService: authenticationService
  ) {}

  get canEditClientNotes(): boolean {
    return this.authService.hasPermission(Permissions.ClientsNotesChange);
  }

  get canEditDriverNotes(): boolean {
    return this.authService.hasPermission(Permissions.DriversNotesChange);
  }

  get openDriverNotesCount() {
    return this.driverNotesTileCount;
  }
  get openClientNotesCount() {
    return this.clientNotesTotalCount;
  }

  get filteredDriverNotes(): DashboardNoteItem[] {
    return this.openDriverNotes;
  }

  get filteredClientNotes(): DashboardNoteItem[] {
    return this.openClientNotes;
  }

  // Stable filter dropdown options (accumulate across loads, never shrink)
  driverNoteTypes: string[] = [];
  driverNoteClients: string[] = [];
  clientNoteTypes: string[] = [];
  clientNoteClients: string[] = [];
  readonly accidentItemTypes: string[] = ['Accident', 'Incident', 'Breakdown', 'Delay', 'Breakdown & follow up'];

  private mergeInto(target: string[], incoming: string[]) {
    const added = [...new Set(incoming)].filter(v => v && !target.includes(v));
    if (added.length) target.push(...added.sort());
  }

  ngOnInit() {
    this.loadDriverNotes();
    this.loadOpenClientNotes();
    this.loadDriverStatus();
    this.loadAccidentsBreakdowns();
  }

  setView(view: ActiveView) {
    this.activeView = view;
    localStorage.setItem(DispatchDashboardComponent.VIEW_KEY, view);
  }

  fetchDriverCards(notes: { driverId?: string }[]) {
    const ids = [...new Set(notes.map((n) => n.driverId).filter(Boolean) as string[])].filter(
      (id) => !this.driverCardCache[id]
    );
    ids.forEach((id) => {
      this.dashboardService.getDriverCard(id).subscribe((data) => {
        this.driverCardCache[id] = data;
      });
    });
  }

  loadDriverNotes() {
    this.driverNotesLoading = true;
    this.dashboardService
      .getDriverNotes({
        includeClosed: this.driverNotesIncludeClosed,
        sortField: this.driverNotesSortField,
        sortDirection: this.driverNotesSortDirection,
        page: this.driverNotesPage,
        pageSize: this.driverNotesPageSize,
        search: this.noteSearch || undefined,
        typeFilter: this.noteTypeFilter || undefined,
        clientFilter: this.noteClientFilter || undefined,
        dateFrom: this.noteDateFrom || undefined,
        dateTo: this.noteDateTo || undefined,
      })
      .subscribe(
        (data) => {
          this.openDriverNotes = data.items;
          this.driverNotesTotalCount = data.totalCount;
          if (this.driverNotesTileCount === 0) {
            this.driverNotesTileCount = data.totalCount;
          }
          this.mergeInto(this.driverNoteTypes, data.items.map(n => n.type));
          this.mergeInto(this.driverNoteClients, data.items.map(n => n.clientName).filter((v): v is string => !!v));
          this.fetchDriverCards(data.items);
          this.driverNotesLoading = false;
        },
        (error) => {
          console.error("Error loading driver notes", error);
          this.driverNotesLoading = false;
        }
      );
  }

  loadOpenClientNotes() {
    this.clientNotesLoading = true;
    this.dashboardService
      .getOpenClientNotes({
        includeClosed: this.clientNoteIncludeClosed,
        sortDirection: "desc",
        page: this.clientNotesPage,
        pageSize: this.clientNotesPageSize,
        search: this.clientNoteSearch || undefined,
        typeFilter: this.clientNoteTypeFilter || undefined,
        clientFilter: this.clientNoteClientFilter || undefined,
        dateFrom: this.clientNoteDateFrom || undefined,
        dateTo: this.clientNoteDateTo || undefined,
      })
      .subscribe(
        (data) => {
          this.openClientNotes = data.items;
          this.clientNotesTotalCount = data.totalCount;
          this.mergeInto(this.clientNoteTypes, data.items.map(n => n.type));
          this.mergeInto(this.clientNoteClients, data.items.map(n => n.clientName).filter((v): v is string => !!v));
          this.clientNotesLoading = false;
        },
        (error) => {
          console.error("Error loading client notes", error);
          this.clientNotesLoading = false;
        }
      );
  }

  loadDriverStatus() {
    this.driverStatusLoading = true;
    this.dashboardService.getDriverStatus().subscribe(
      (data) => {
        this.driverStatus = data;
        this.driverStatusLoading = false;
      },
      (error) => {
        console.error("Error loading driver status", error);
        this.driverStatusLoading = false;
      }
    );
  }

  loadAccidentsBreakdowns() {
    this.accidentBreakdownsLoading = true;
    this.dashboardService
      .getAccidentsBreakdowns({
        includeClosed: this.accidentIncludeClosed,
        sortDirection: "desc",
        page: this.accidentPage,
        pageSize: this.accidentPageSize,
        search: this.accidentSearch || undefined,
        typeFilter: this.accidentTypeFilter || undefined,
        dateFrom: this.accidentDateFrom || undefined,
        dateTo: this.accidentDateTo || undefined,
      })
      .subscribe(
        (data) => {
          this.accidentBreakdowns = data.items;
          this.accidentTotalCount = data.totalCount;
          if (this.accidentTileCount === 0) {
            this.accidentTileCount = data.totalCount;
          }
          this.fetchDriverCards(data.items);
          this.accidentBreakdownsLoading = false;
        },
        (error) => {
          console.error("Error loading accidents/breakdowns", error);
          this.accidentBreakdownsLoading = false;
        }
      );
  }

  // Driver notes pagination & sorting
  onDriverNotesPageChange(event: any) {
    this.driverNotesPage = event.page;
    this.driverNotesPageSize = event.rows;
    this.loadDriverNotes();
  }

  sortDriverNotes(field: string) {
    if (this.driverNotesSortField === field) {
      this.driverNotesSortDirection = this.driverNotesSortDirection === "asc" ? "desc" : "asc";
    } else {
      this.driverNotesSortField = field;
      this.driverNotesSortDirection = "asc";
    }
    this.driverNotesPage = 0;
    this.loadDriverNotes();
  }

  getSortIcon(field: string): string {
    if (this.driverNotesSortField !== field) return "fa fa-sort text-muted";
    return this.driverNotesSortDirection === "asc" ? "fa fa-sort-asc" : "fa fa-sort-desc";
  }

  onIncludeClosedChange() {
    this.driverNotesPage = 0;
    this.loadDriverNotes();
  }

  applyDriverNoteFilter() {
    this.driverNotesPage = 0;
    this.loadDriverNotes();
  }

  clearDriverNoteFilters() {
    this.noteSearch = "";
    this.noteTypeFilter = "";
    this.noteClientFilter = "";
    this.noteDateFrom = "";
    this.noteDateTo = "";
    this.driverNotesPage = 0;
    this.loadDriverNotes();
  }

  // Accidents/Breakdowns filters
  applyAccidentFilter() {
    this.accidentPage = 0;
    this.loadAccidentsBreakdowns();
  }

  clearAccidentFilters() {
    this.accidentSearch = "";
    this.accidentTypeFilter = "";
    this.accidentDateFrom = "";
    this.accidentDateTo = "";
    this.accidentPage = 0;
    this.loadAccidentsBreakdowns();
  }

  onAccidentIncludeClosedChange() {
    this.accidentPage = 0;
    this.loadAccidentsBreakdowns();
  }

  onAccidentPageChange(event: any) {
    this.accidentPage = event.page;
    this.accidentPageSize = event.rows;
    this.loadAccidentsBreakdowns();
  }

  // Client notes filters
  applyClientNoteFilter() {
    this.clientNotesPage = 0;
    this.loadOpenClientNotes();
  }

  clearClientNoteFilters() {
    this.clientNoteSearch = "";
    this.clientNoteTypeFilter = "";
    this.clientNoteClientFilter = "";
    this.clientNoteDateFrom = "";
    this.clientNoteDateTo = "";
    this.clientNotesPage = 0;
    this.loadOpenClientNotes();
  }

  onClientNoteIncludeClosedChange() {
    this.clientNotesPage = 0;
    this.loadOpenClientNotes();
  }

  onClientNotePageChange(event: any) {
    this.clientNotesPage = event.page;
    this.clientNotesPageSize = event.rows;
    this.loadOpenClientNotes();
  }

  isAccidentType(item: AccidentBreakdownItem): boolean {
    const t = item.type?.toLowerCase() ?? "";
    return t.includes("accident") || t.includes("incident");
  }

  viewDriverNote(note: { id: string }) {
    this.noteDetailLoading = true;
    this.dashboardService.getFullDriverNote(note.id).subscribe(
      (fullNote: DriverNote) => {
        this.selectedDriverNote = fullNote;
        this.driverNoteModalVisible = true;
        this.noteDetailLoading = false;
      },
      (error) => {
        console.error("Error loading note detail", error);
        this.noteDetailLoading = false;
      }
    );
  }

  viewClientNote(note: DashboardNoteItem) {
    this.noteDetailLoading = true;
    this.dashboardService.getFullClientNote(note.id).subscribe(
      (fullNote: ClientNoteViewModel) => {
        this.selectedClientNote = fullNote;
        this.clientNoteModalVisible = true;
        this.noteDetailLoading = false;
      },
      (error) => {
        console.error("Error loading note detail", error);
        this.noteDetailLoading = false;
      }
    );
  }

  onDriverNoteModalClosed() {
    this.driverNoteModalVisible = false;
    this.selectedDriverNote = null;
  }

  onDriverNoteSaved(_note: DriverNote) {
    this.loadDriverNotes();
  }

  onClientNoteModalClosed() {
    this.clientNoteModalVisible = false;
    this.selectedClientNote = null;
  }

  onClientNoteSaved(saved: ClientNoteViewModel) {
    // Update the note in the local list so the email/status indicator reflects immediately
    const idx = this.openClientNotes.findIndex(n => n.id === saved.id);
    if (idx !== -1) {
      this.openClientNotes[idx] = {
        ...this.openClientNotes[idx],
        status: saved.status,
        type: saved.type,
        note: saved.note,
      };
    }
    this.loadOpenClientNotes();
  }

  getDriverPictureUrl(driver: DriverListing): string {
    if (driver.picture) {
      return this.getPictureService.getPicturePath(driver.picture, "driver");
    }
    return "assets/img/tractordriver.jpg";
  }

  isFollowUpToday(date: Date): boolean {
    if (!date) return false;
    const d = new Date(date);
    const today = new Date();
    return (
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate()
    );
  }

  isFollowUpOverdue(date: Date): boolean {
    if (!date) return false;
    const d = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return d < today;
  }

  getNoteTypeClass(type: string): string {
    if (!type) return "";
    const t = type.toLowerCase();
    if (t.includes("breakdown")) return "type-breakdown";
    if (t.includes("accident")) return "type-accident";
    if (t.includes("delay")) return "type-delay";
    if (t.includes("incident")) return "type-incident";
    if (t.includes("release")) return "type-release";
    return "type-general";
  }

  joinAccidentLocation(city: string, state: string, location: string): string {
    const parts = [city, state].filter((v) => !!v).join(", ");
    if (location && parts) return `${parts} - ${location}`;
    return location || parts;
  }

  formatAccidentDate(date: string, time: string): string {
    if (!date) return null;
    // date is stored as a string (e.g. "2026-03-01"), time as "14:30" or "2:30 PM" etc.
    const combined = time ? `${date} ${time}` : date;
    const d = new Date(combined);
    if (isNaN(d.getTime())) return [date, time].filter(Boolean).join(" ");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return time ? `${month}/${day}/${year} ${hours}:${minutes} ${ampm}` : `${month}/${day}/${year}`;
  }
}
